import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {SegmentChangeSource, ValuationNotifications} from "@reva/shared/valuation-data";

import type {RevaThunkAction} from "./revaHooks";

interface NotificationsState {
    notifyAddressChangedFromOrderApi: boolean;
    notifyAddressChangedFromOrderApiPreviousAddress: string | null;
    notifyOrderAddressCouldNotBeAdopted: boolean;
    orderAddressCouldNotBeAdoptedFailedAddress: string | null;
    orderAddressCouldNotBeAdoptedWasReferenceAddress: boolean;
    /** True when the backend reported >1 energy certificate with an empty IDP layer (decision required). */
    notifyEnergyCertificateDecisionRequired: boolean;
    /** True when an external source moved the effective segment and the REVA overrides were discarded (#19444). */
    notifySegmentChanged: boolean;
    segmentChangedPreviousSegment: string | null;
    segmentChangedSource: SegmentChangeSource | null;
}

/** Payload for {@link setNotifyAddressChangedFromOrderApi}. */
interface SetNotifyAddressChangedFromOrderApiPayload {
    enabled: boolean;
    previousAddress?: string | null;
}

/** Payload for {@link setNotifyOrderAddressCouldNotBeAdopted}. */
interface SetNotifyOrderAddressCouldNotBeAdoptedPayload {
    enabled: boolean;
    failedAddress?: string | null;
    wasReferenceAddress?: boolean;
}

/** Payload für {@link setNotifySegmentChanged}. */
interface SetNotifySegmentChangedPayload {
    enabled: boolean;
    previousSegment?: string | null;
    source?: SegmentChangeSource | null;
}

const initialState: NotificationsState = {
    notifyAddressChangedFromOrderApi: false,
    notifyAddressChangedFromOrderApiPreviousAddress: null,
    notifyOrderAddressCouldNotBeAdopted: false,
    orderAddressCouldNotBeAdoptedFailedAddress: null,
    orderAddressCouldNotBeAdoptedWasReferenceAddress: false,
    notifyEnergyCertificateDecisionRequired: false,
    notifySegmentChanged: false,
    segmentChangedPreviousSegment: null,
    segmentChangedSource: null,
};

const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        resetNotifications: (state) => {
            state.notifyAddressChangedFromOrderApi = false;
            state.notifyAddressChangedFromOrderApiPreviousAddress = null;
            state.notifyOrderAddressCouldNotBeAdopted = false;
            state.orderAddressCouldNotBeAdoptedFailedAddress = null;
            state.orderAddressCouldNotBeAdoptedWasReferenceAddress = false;
            state.notifyEnergyCertificateDecisionRequired = false;
            state.notifySegmentChanged = false;
            state.segmentChangedPreviousSegment = null;
            state.segmentChangedSource = null;
        },
        setNotifyAddressChangedFromOrderApi: (
            state,
            action: PayloadAction<SetNotifyAddressChangedFromOrderApiPayload>,
        ) => {
            const {enabled, previousAddress} = action.payload;
            state.notifyAddressChangedFromOrderApi = enabled;
            state.notifyAddressChangedFromOrderApiPreviousAddress = enabled ? (previousAddress ?? null) : null;
        },
        setNotifyOrderAddressCouldNotBeAdopted: (
            state,
            action: PayloadAction<SetNotifyOrderAddressCouldNotBeAdoptedPayload>,
        ) => {
            const {enabled, failedAddress, wasReferenceAddress} = action.payload;
            state.notifyOrderAddressCouldNotBeAdopted = enabled;
            state.orderAddressCouldNotBeAdoptedFailedAddress = enabled ? (failedAddress ?? null) : null;
            state.orderAddressCouldNotBeAdoptedWasReferenceAddress = enabled ? (wasReferenceAddress ?? false) : false;
        },
        setNotifyEnergyCertificateDecisionRequired: (state, action: PayloadAction<boolean>) => {
            state.notifyEnergyCertificateDecisionRequired = action.payload;
        },
        setNotifySegmentChanged: (state, action: PayloadAction<SetNotifySegmentChangedPayload>) => {
            const {enabled, previousSegment, source} = action.payload;
            state.notifySegmentChanged = enabled;
            state.segmentChangedPreviousSegment = enabled ? (previousSegment ?? null) : null;
            state.segmentChangedSource = enabled ? (source ?? null) : null;
        },
    },
    selectors: {
        selectNotifyAddressChangedFromOrderApi: (state) => state.notifyAddressChangedFromOrderApi,
        selectNotifyAddressChangedFromOrderApiPreviousAddress: (state) =>
            state.notifyAddressChangedFromOrderApiPreviousAddress,
        selectNotifyOrderAddressCouldNotBeAdopted: (state) => state.notifyOrderAddressCouldNotBeAdopted,
        selectOrderAddressCouldNotBeAdoptedFailedAddress: (state) => state.orderAddressCouldNotBeAdoptedFailedAddress,
        selectOrderAddressCouldNotBeAdoptedWasReferenceAddress: (state) =>
            state.orderAddressCouldNotBeAdoptedWasReferenceAddress,
        selectNotifyEnergyCertificateDecisionRequired: (state) => state.notifyEnergyCertificateDecisionRequired,
        selectNotifySegmentChanged: (state) => state.notifySegmentChanged,
        selectSegmentChangedPreviousSegment: (state) => state.segmentChangedPreviousSegment,
        selectSegmentChangedSource: (state) => state.segmentChangedSource,
    },
});

export const {
    resetNotifications,
    setNotifyAddressChangedFromOrderApi,
    setNotifyOrderAddressCouldNotBeAdopted,
    setNotifyEnergyCertificateDecisionRequired,
    setNotifySegmentChanged,
} = notificationsSlice.actions;

export const {
    selectNotifyAddressChangedFromOrderApi,
    selectNotifyAddressChangedFromOrderApiPreviousAddress,
    selectNotifyOrderAddressCouldNotBeAdopted,
    selectOrderAddressCouldNotBeAdoptedFailedAddress,
    selectOrderAddressCouldNotBeAdoptedWasReferenceAddress,
    selectNotifyEnergyCertificateDecisionRequired,
    selectNotifySegmentChanged,
    selectSegmentChangedPreviousSegment,
    selectSegmentChangedSource,
} = notificationsSlice.selectors;

export default notificationsSlice.reducer;

/**
 * Handle backend notification from a persistence API call.
 */
export const handleNotificationsFromPersistence =
    (notifications: ValuationNotifications | null): RevaThunkAction<Promise<void>> =>
    async (dispatch) => {
        if (!notifications) return;

        if (typeof notifications.orderAddressChanged === "object") {
            dispatch(
                setNotifyAddressChangedFromOrderApi({
                    enabled: notifications.orderAddressChanged.notified,
                    previousAddress: notifications.orderAddressChanged.previousAddress,
                }),
            );
        }

        if (typeof notifications.orderAddressCouldNotBeAdopted === "object") {
            dispatch(
                setNotifyOrderAddressCouldNotBeAdopted({
                    enabled: notifications.orderAddressCouldNotBeAdopted.notified,
                    failedAddress: notifications.orderAddressCouldNotBeAdopted.failedAddressQuery,
                    wasReferenceAddress: notifications.orderAddressCouldNotBeAdopted.wasReferenceAddress,
                }),
            );
        }

        dispatch(
            setNotifyEnergyCertificateDecisionRequired(
                notifications.energyCertificateDecisionRequired?.notified === true,
            ),
        );

        if (typeof notifications.segmentChanged === "object") {
            dispatch(
                setNotifySegmentChanged({
                    enabled: notifications.segmentChanged.notified,
                    previousSegment: notifications.segmentChanged.previousSegment,
                    source: notifications.segmentChanged.source,
                }),
            );
        }
    };
